import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdatabaseAuto, IdeleteOneResponse, IfilterProps,
  Iuser
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';

/**
 * Describes a flagged user activity considered suspicious, capturing
 * the actor, originating IP, and the reason for the flag.
 *
 * @public
 */
export interface IsuspiciousActivity
  extends IdatabaseAuto {

  /** The user associated with the suspicious activity. */
  user: Iuser;

  /** IP address from which the suspicious activity originated. */
  ip: string;

  /** Reason explaining why the activity was flagged. */
  reason: string;
}

/**
 * Client-side model for interacting with the Suspicious Activity API.
 *
 * Provides helpers to list, filter, fetch, and delete suspicious activity records.
 *
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UserSuspiciousActivity} class when invoking client operations.
 *
 * @example
 * ```typescript
 * const result = await UserSuspiciousActivity.getAll();
 *
 * const result = await UserSuspiciousActivity.filterAll({
 *   // filter criteria
 * });
 *
 * const result = await UserSuspiciousActivity.bulkDelete({
 *   // delete criteria
 * });
 *
 * const suspiciousActivity = await UserSuspiciousActivity.getOne('resourceId');
 *
 * const result = await UserSuspiciousActivity.remove();
 * ```
 */
export class UserSuspiciousActivity
  extends DatabaseAuto {
  /** Base path for suspicious activity routes. */
  private static readonly baseUrl = '/suspicious-activity';

  /** The user associated with the suspicious activity. */
  readonly user: IsuspiciousActivity['user'];

  /** IP address from which the suspicious activity originated. */
  readonly ip: IsuspiciousActivity['ip'];

  /** Reason explaining why the activity was flagged. */
  readonly reason: IsuspiciousActivity['reason'];


  /**
   * Constructs an instance of {@link UserSuspiciousActivity}.
   *
   * @param data - The suspicious-activity data used to initialize the instance.
   */
  constructor(data: IsuspiciousActivity) {
    super(data);

    this.user = data.user;
    this.ip = data.ip;
    this.reason = data.reason;
  }

  /**
   * Retrieves all suspicious activity items with pagination support.
   *
   * @param offset - The starting point for pagination. Defaults to `0`.
   * @param limit - The number of items to retrieve. Defaults to `20`.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserSuspiciousActivity} instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = AuthClient.ehttp
      .makeGet<IdataArrayResponse<IsuspiciousActivity>>(`${UserSuspiciousActivity.baseUrl}?${query}`);
    const suspiciousActivities = await lastValueFrom(observer$);

    return {
      count: suspiciousActivities.count,
      dataArr: suspiciousActivities.data.map((val) => new UserSuspiciousActivity(val))
    };
  }

  /**
   * Filters suspicious activity based on the provided filter properties.
   *
   * @param filter - The filter criteria for retrieving suspicious activity.
   * @returns A promise that resolves to the total count and an array of
   * {@link UserSuspiciousActivity} instances matching the filter.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp
      .makePost<IdataArrayResponse<IsuspiciousActivity>>(`${UserSuspiciousActivity.baseUrl}/filter`, filter);
    const suspiciousActivities = await lastValueFrom(observer$);

    return {
      count: suspiciousActivities.count,
      dataArr: suspiciousActivities.data.map((val) => new UserSuspiciousActivity(val))
    };
  }

  /**
   * Retrieves a single suspicious activity item by resource identifier.
   *
   * @param resourceId - The unique identifier for the suspicious activity to retrieve.
   * @returns A promise that resolves to the {@link UserSuspiciousActivity} instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AuthClient.ehttp
      .makeGet<IsuspiciousActivity>(`${UserSuspiciousActivity.baseUrl}/${resourceId}`);
    const suspiciousActivity = await lastValueFrom(observer$);

    return new UserSuspiciousActivity(suspiciousActivity);
  }

  /**
   * Removes multiple suspicious activity items based on the provided criteria.
   *
   * @param payload - The criteria for deleting multiple suspicious activity items.
   * @returns A promise that resolves to the bulk delete operation result.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = AuthClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${UserSuspiciousActivity.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current suspicious activity item.
   *
   * @returns A promise that resolves to the delete operation result, or `null`.
   */
  remove() {
    const observer$ = AuthClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${UserSuspiciousActivity.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
